import { useState } from "react";
import { useApp } from "../../context/AppContext.js";
import { fmtDateShort } from "../../utils/dateHelpers.js";
import { fmt } from "../../utils/formatters.js";
import { sortEntries, type EntrySort } from "../../utils/entrySort.js";

export function Entries() {
  const {
    entries, clients,
    entriesSortBy, setEntriesSortBy,
    entriesAuthorFilter, setEntriesAuthorFilter, allUsers,
    setShowAddEntry,
    setEditEntry,
    handleDeleteEntry,
  } = useApp();

  const [filterClient, setFilterClient] = useState("");

  const activeClients = clients.filter((c) => c.status === "active");
  const getClient = (id: string) => clients.find((c) => c.id === id);

  const originalOrder = entries.map((e) => e.id);
  const filtered = entries.filter(
    (e) =>
      (!filterClient || e.clientId === filterClient) &&
      (!entriesAuthorFilter || e.addedBy === entriesAuthorFilter),
  );
  const sorted = sortEntries(filtered, entriesSortBy, originalOrder);

  const handleEdit = (entry: typeof entries[0]) => {
    setEditEntry(entry);
    setShowAddEntry(true);
  };

  const handleDelete = (entryId: string) => {
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return;
    const client = getClient(entry.clientId);
    const msg = `Delete this entry?\n\nClient: ${client?.name ?? "Unknown"}\nTask: ${entry.task}\nDate: ${fmtDateShort(entry.date)}`;
    if (!confirm(msg)) return;
    void handleDeleteEntry(entryId);
  };

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <select
          className="sel"
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
        >
          <option value="">All Clients</option>
          {activeClients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className="sel"
          value={entriesAuthorFilter}
          onChange={(e) => setEntriesAuthorFilter(e.target.value)}
        >
          <option value="">All Authors</option>
          {allUsers.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
        <select
          className="sel"
          value={entriesSortBy}
          onChange={(e) => setEntriesSortBy(e.target.value as EntrySort)}
        >
          <option value="date-desc">Date (Newest)</option>
          <option value="date-asc">Date (Oldest)</option>
          <option value="created-desc">Added (Newest)</option>
          <option value="created-asc">Added (Oldest)</option>
        </select>
        <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--gray-500)" }}>
          {sorted.length} entries
        </span>
      </div>

      {/* Table */}
      <div className="card">
        <table>
          <thead>
            <tr>
              {["Date", "Client", "Task", "Hours", "Charge", "Added By", ""].map((h, i) => (
                <th key={i} className={i >= 3 && i <= 4 ? "right" : ""}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => {
              const client = getClient(e.clientId);
              return (
                <tr key={e.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDateShort(e.date)}</td>
                  <td>
                    <div className="client-row">
                      <span className="dot" style={{ background: client?.color ?? "#ccc" }} />
                      {client?.name ?? "Unknown"}
                    </div>
                  </td>
                  <td>{e.task}</td>
                  <td className="right">{e.hours > 0 ? `${e.hours}h` : "—"}</td>
                  <td className="right">{e.taskCharge > 0 ? fmt(e.taskCharge) : "—"}</td>
                  <td style={{ fontSize: 13, color: "var(--gray-500)" }}>{e.addedBy}</td>
                  <td className="right" style={{ whiteSpace: "nowrap" }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(e)}>Edit</button>{" "}
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--gray-400)", padding: 32 }}>
                  No entries found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
